import MarkdownPreview from "@uiw/react-markdown-preview";
import _ from "lodash";
import { Fragment, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { createQuotationUploadMode } from "../../../api/quotation";
import { brand } from "../../../context";
import { NotificationBanner } from "../../banners/NotificationBanner";
import { BackButton } from "./componets";
import { formatFileSize } from "./helpers";
import { QuotationRequestDocumentParsingLoader } from "./QuotationRequestLoader";

type UploadFileFormProps = {
  file: File | null;
  onFileChange: (file: File | null) => void;
};

function UploadFileForm({ file, onFileChange }: UploadFileFormProps) {
  useEffect(() => {
    onFileChange(null);
  }, [onFileChange]);

  return (
    <div className="grid gap-1">
      <label className="font-medium" htmlFor="file">
        Upload File {!!file ? `(Size: ${formatFileSize(file.size)})` : ""}
      </label>
      <input
        className="border border-gray-300 rounded-md p-1 cursor-pointer"
        type="file"
        name="file"
        // accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg,image/jpg"
        accept="application/pdf,image/png,image/jpeg,image/jpg"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFileChange(file);
        }}
      />
    </div>
  );
}

// function base64ToBytes(base64: string) {
//   const binString = window.atob(base64);
//   return Uint8Array.from(binString, (m) => m.codePointAt(0) || 0);
// }

async function SSEClient(
  file: File,
  setText: (text: string | ((prev: string | null) => string | null)) => void,
  setSSEDone: () => void,
) {
  const response = await createQuotationUploadMode({ file });
  const reader = response.body?.getReader();
  if (!reader) {
    setSSEDone();
    return;
  }

  const decoder = new TextDecoder("utf-8");

  let parsingError = false;
  while (true) {
    const { value, done } = await reader.read();

    if (done || parsingError) {
      setSSEDone();
      break;
    }

    const text = decoder.decode(value, { stream: true });
    const textArray = text.split("\n");
    for (const line of textArray) {
      if (line.trim() === "") continue;
      if (line.startsWith("data:") || line.startsWith("event:")) continue;

      try {
        // const txtData = line.replace("data:", "");
        const txtData = window.atob(line);

        setText((prev) => prev + txtData);
        // setText(txtData);
      } catch {
        parsingError = true;
        break;
      }
    }
  }
}

type UploadQuotationRequestDocProps = {
  file: File | null;
  onSetParsedFile: (parsedFile: string) => void;
  onFileChange: (file: File | null) => void;
  onBack: () => void;
};
export function UploadQuotationRequestDoc({
  file,
  onFileChange,
  onSetParsedFile,
  onBack,
}: UploadQuotationRequestDocProps) {
  const [sseDone, setSSEDone] = useState(false);
  const [text, setText] = useState<string | null>(null);

  // Move into the next step when the SSE is done and the text is not empty
  useEffect(() => {
    if (sseDone && !!text) {
      onSetParsedFile(text);
      return;
    }

    if (sseDone && !text) {
      toast.error("Failed to parse the document. Please try again.");
      onBack();
    }
  }, [sseDone, text, onSetParsedFile, onBack]);

  // Auto scroll to the bottom of the parsed file when the text is updated
  useEffect(() => {
    if (parsedFileContainer.current && !!text) {
      parsedFileContainer.current.scrollTo({
        top: parsedFileContainer.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [text]);

  const parsedFileContainer = useRef<HTMLDivElement>(null);

  return text !== null && text === "" ? (
    <QuotationRequestDocumentParsingLoader />
  ) : !!text ? (
    <div className="Modal__card__body overflow-hidden grid grid-rows-[auto_1fr]">
      <h6>
        Preview Document <br />
        <span className="text-sm text-gray-500">
          This might take a while, please wait for the document to be parsed.
        </span>
      </h6>
      <div
        ref={parsedFileContainer}
        className="grid border border-gray-200 rounded-md p-3 overflow-auto [&_h2]:text-xl! [&_h3]:text-lg! [&_h4]:text-base! [&_h5]:text-base! [&_h6]:text-base!"
      >
        <MarkdownPreview
          source={text}
          style={{ padding: 16, maxWidth: "50vw" }}
          wrapperElement={{
            "data-color-mode": "light",
          }}
        />
      </div>
    </div>
  ) : (
    <Fragment>
      <div className="Modal__card__body grid auto-rows-max">
        <h6>Upload Quotation Request Document</h6>
        <NotificationBanner
          variant="info"
          title="Please note that the document will be parsed and the information will be used to create a quotation request."
          description="The document should be in PDF, Word, PNG, JPG, or JPEG format. Please don't upload documents larger than 500KB when uploading documents and larger than 2MB when uploading images."
        />
        <UploadFileForm file={file} onFileChange={onFileChange} />
      </div>
      <div className="Modal__card__actions">
        <BackButton onBack={onBack} />
        <button
          className="Button"
          type="button"
          data-brand={brand}
          onClick={async (e) => {
            e.preventDefault();
            if (!file) {
              toast.warning("Please choose a file first.");
              return;
            }

            setText("");
            await SSEClient(file, setText, () => {
              setSSEDone(true);
            });
          }}
        >
          Submit Document
        </button>
      </div>
    </Fragment>
  );
}
